import React, { ReactNode } from "react";

import Header from "@common/components/Header/Header";
import Navigation from "@common/components/Navigation/Navigation";
import { ScrollContainer } from "@common/components/ScrollContainer/ScrollContainer";
import { App, Workspace } from "@common/models";
import { useAppContext } from "@main/context/App/App.context";
import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { SubscriptionState, useSubscriptionContext } from "@main/context/Subscription/SubscriptionContext";
import { useScreenDimensions } from "@main/pages/shared/consumers/useScreenDimensions";
import { useCurrentPathRouteConfig } from "@main/router/util/useCurrentPathRouteConfig";

import AppSwitcher from "./AppSwitcher";
import PageName from "./PageName";

export const PAGE_NAVIGATION_ELEMENT_ROOT_ID = "page-navigation-element-root";
export const ELEMENT_CONTAINER_ID = "element-container-main";
export const NAV_LIST = "nav_list";

export function PageNavigation({ children }: { children: ReactNode }) {
  const { apps } = useAppContext();
  const { selectedApp, selectedWorkspace } = useAppSelectionContext();
  const { state: subState } = useSubscriptionContext();

  if (!selectedWorkspace || !selectedApp || subState !== SubscriptionState.good)
    return <PageNavigationSkeleton>{children}</PageNavigationSkeleton>;

  return (
    <PageNavigationInternal
      selectedApp={selectedApp}
      selectedWorkspace={selectedWorkspace}
      appList={apps.filter((a) => a.active)}
    >
      {children}
    </PageNavigationInternal>
  );
}

interface InternalProps {
  selectedApp: App;
  appList: App[];
  selectedWorkspace: Workspace;
  children: ReactNode;
}

const PageNavigationInternal = ({ children, selectedApp, appList }: InternalProps) => {
  const { loading } = useAppContext();
  const { hasBorderBottom } = useCurrentPathRouteConfig();

  const { shouldHideLayoutViews } = useScreenDimensions();

  return (
    <>
      {!loading && (
        <Header
          bottomBorder={hasBorderBottom}
          styles={{
            display: shouldHideLayoutViews ? "none" : "flex",
          }}
        >
          <Navigation elementId={NAV_LIST}>
            <PageName />
            <AppSwitcher
              selectedApp={selectedApp}
              appList={appList}
            />
          </Navigation>
        </Header>
      )}
      <ScrollContainer>{children}</ScrollContainer>
    </>
  );
};

const PageNavigationSkeleton = ({ children }: { children: ReactNode }) => {
  return (
    <>
      <Header></Header>
      {children}
    </>
  );
};
